import React from "react";
import { TriangleAlert, TrendingUp, Info } from "lucide-react";
import { CampaignItem } from "../../model/Campaign";
import { eur, pct, marginTone, TONE } from "../../helpers/format";

interface CampaignItemErpPanelProps {
  item: CampaignItem;
  isAdmin: boolean;
  /** Hide the "Configured vs ERP" comparison (e.g. inside the add/edit modal). */
  showErpComparison?: boolean;
}

function MarginCard({
  label,
  before,
  beforePct,
  after,
  afterPct,
}: {
  label: string;
  before: number | null | undefined;
  beforePct: number | null | undefined;
  after?: number | null;
  afterPct?: number | null;
}) {
  if (before == null) return null;
  const hasImpact =
    after != null &&
    afterPct != null &&
    (after !== before || afterPct !== beforePct);
  const current = after ?? before;
  const currentPct = afterPct ?? beforePct;
  const tone = marginTone(currentPct);
  const Icon = tone === "neg" ? TriangleAlert : TrendingUp;
  const deltaValue = hasImpact ? current - before : null;
  const deltaPct =
    hasImpact && beforePct != null ? currentPct! - beforePct : null;

  return (
    <article className={`rounded-lg border p-3 ${TONE[tone]}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-600">
        {label}
      </p>
      <p className="mt-1 text-lg font-semibold tabular-nums">
        {hasImpact && (
          <span className="mr-2 text-sm font-normal text-gray-500 line-through tabular-nums">
            {eur.format(before)}
          </span>
        )}
        {eur.format(current)}
      </p>
      {currentPct != null && (
        <p className="text-sm font-medium tabular-nums">
          <Icon className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
          {pct.format(currentPct)}
          {deltaValue != null && deltaPct != null && (
            <span className="ml-2 text-xs font-normal">
              Δ {deltaValue > 0 ? "+" : ""}
              {eur.format(deltaValue)} · {deltaPct > 0 ? "+" : ""}
              {pct.format(deltaPct)}
            </span>
          )}
        </p>
      )}
    </article>
  );
}

const CampaignItemErpPanel: React.FC<CampaignItemErpPanelProps> = ({
  item,
  isAdmin,
  showErpComparison = true,
}) => {
  const c = item.compound;

  const rows = [
    {
      label: "Custo",
      configured: c?.base_cost_price,
      erp: c?.erp_base_cost_price,
      field: "cost",
      adminOnly: true,
    },
    {
      label: "Preço Reseller",
      configured: c?.base_retail_price,
      erp: c?.erp_base_retail_price,
      field: "base_retail_price",
      adminOnly: false,
    },
    {
      label: "PVP s/IVA",
      configured: c?.price_pvp_without_vat,
      erp: c?.erp_price_pvp_without_vat,
      field: "pvp_without_vat",
      adminOnly: false,
    },
    {
      label: "PVP c/IVA",
      configured: c?.price_pvp_with_vat,
      erp: c?.erp_price_pvp_with_vat,
      field: "pvp_with_vat",
      adminOnly: false,
    },
  ].filter((r) => !r.adminOnly || isAdmin);

  return (
    <div
      className={`grid grid-cols-1 gap-4 ${
        showErpComparison ? "lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]" : ""
      }`}
    >
      <div className="grid grid-cols-[repeat(auto-fit,minmax(11rem,1fr))] gap-3">
        {isAdmin && (
          <MarginCard
            label="Margem Niposom"
            before={c?.niposom_margin_value}
            beforePct={c?.niposom_margin_pct}
            after={c?.niposom_promo_margin_value}
            afterPct={c?.niposom_promo_margin_pct}
          />
        )}
        <MarginCard
          label="Margem Reseller"
          before={c?.reseller_margin_value}
          beforePct={c?.reseller_margin_pct}
          after={c?.reseller_promo_margin_value}
          afterPct={c?.reseller_promo_margin_pct}
        />
      </div>

      {showErpComparison && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Configured vs ERP
            </p>
            {c?.erp_synced_at && (
              <time
                className="text-xs text-gray-500"
                dateTime={c.erp_synced_at}
              >
                ERP sync: {new Date(c.erp_synced_at).toLocaleString("pt-PT")}
              </time>
            )}
          </div>
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th scope="col" className="pb-1 pr-2 font-medium">
                    Campo
                  </th>
                  <th scope="col" className="pb-1 pr-2 font-medium text-right">
                    Configured
                  </th>
                  <th scope="col" className="pb-1 pr-2 font-medium text-right">
                    ERP
                  </th>
                  <th scope="col" className="pb-1 pr-2 font-medium text-right">
                    Δ
                  </th>
                  <th scope="col" className="pb-1 font-medium">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const hasBoth = r.configured != null && r.erp != null;
                  const delta = hasBoth ? r.configured! - r.erp! : null;
                  const mismatched = c?.mismatch_fields?.includes(r.field);
                  return (
                    <tr key={r.field} className="border-b">
                      <th
                        scope="row"
                        className="py-1 pr-2 font-medium text-left"
                      >
                        {r.label}
                      </th>
                      <td className="py-1 pr-2 text-right tabular-nums">
                        {r.configured != null ? eur.format(r.configured) : "—"}
                      </td>
                      <td className="py-1 pr-2 text-right tabular-nums">
                        {r.erp != null ? eur.format(r.erp) : "—"}
                      </td>
                      <td className="py-1 pr-2 text-right tabular-nums">
                        {delta != null ? eur.format(delta) : "—"}
                      </td>
                      <td className="py-1">
                        {mismatched ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-300">
                            <TriangleAlert
                              className="h-3.5 w-3.5"
                              aria-hidden="true"
                            />
                            desalinhado
                          </span>
                        ) : r.erp == null ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            sem dados
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                            ok
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 flex items-start gap-1 text-xs text-gray-500">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Um desconto na margem Niposom reduz o preço de compra do reseller,
            não apenas o PVP de exibição.
          </p>
        </div>
      )}
    </div>
  );
};

export default CampaignItemErpPanel;
